import React, { useEffect, useMemo, useRef, useState } from 'react';
import { alertsPanelCSS } from './AlertsPanel.css';

const STATUSES = [
    { value: '',         label: 'Todas' },
    { value: 'open',     label: 'Abiertas' },
    { value: 'silenced', label: 'Silenciadas' },
    { value: 'ignored',  label: 'Ignoradas' },
    { value: 'resolved', label: 'Resueltas' },
];

const RULE_LABELS = {
    registro_sin_avance: 'Registro sin avance',
    buro_exitoso_sin_documentos: 'Buró exitoso sin documentos',
    documentos_incompletos_24h: 'Documentos incompletos hace 24h',
};

const RULE_ICONS = {
    registro_sin_avance: 'pi pi-user',
    buro_exitoso_sin_documentos: 'pi pi-id-card',
    documentos_incompletos_24h: 'pi pi-file',
};

const STATUS_LABELS = {
    open: 'Abierta',
    silenced: 'Silenciada',
    ignored: 'Ignorada',
    resolved: 'Resuelta',
};

const SEVERITY_MAP = {
    critical: { bg: '#fee2e2', fg: '#991b1b', border: '#ef4444', label: 'Crítica' },
    error:    { bg: '#fee2e2', fg: '#991b1b', border: '#ef4444', label: 'Error' },
    high:     { bg: '#ffedd5', fg: '#9a3412', border: '#f97316', label: 'Alta' },
    warning:  { bg: '#fef3c7', fg: '#92400e', border: '#f59e0b', label: 'Adv.' },
    medium:   { bg: '#fef3c7', fg: '#92400e', border: '#f59e0b', label: 'Media' },
    low:      { bg: '#dbeafe', fg: '#1e40af', border: '#3b82f6', label: 'Baja' },
    info:     { bg: '#e0f2fe', fg: '#075985', border: '#34C6F1', label: 'Info' },
};

const QUICK_SILENCE = [
    { label: '1h', hours: 1 },
    { label: '4h', hours: 4 },
    { label: '24h', hours: 24 },
    { label: '7d', hours: 168 },
];

function timeAgo(iso) {
    if (!iso) return '—';
    const diffSec = (Date.now() - new Date(iso).getTime()) / 1000;
    if (Number.isNaN(diffSec) || diffSec < 0) return '—';
    if (diffSec < 60) return 'hace unos segundos';
    if (diffSec < 3600) return `hace ${Math.floor(diffSec / 60)} min`;
    if (diffSec < 86400) return `hace ${Math.floor(diffSec / 3600)} h`;
    return `hace ${Math.floor(diffSec / 86400)} d`;
}

function sevFor(severity) {
    const key = String(severity || 'info').toLowerCase();
    return SEVERITY_MAP[key] || SEVERITY_MAP.info;
}

function ruleLabel(code) {
    return RULE_LABELS[code] || code;
}

function ruleIcon(code) {
    return RULE_ICONS[code] || 'pi pi-bell';
}

export default function AlertsPanel({
    visible,
    onClose,
    alerts,
    loading,
    pending = {},
    onRefresh,
    onIgnore,
    onSilence,
    onResolve,
    onMarkSeen,
}) {
    const [statusFilter, setStatusFilter] = useState('');
    const [search, setSearch] = useState('');
    const [silencePopFor, setSilencePopFor] = useState(null);
    const [customHours, setCustomHours] = useState('');
    const popRef = useRef(null);
    const listRef = useRef(null);

    useEffect(() => {
        if (!silencePopFor) return;
        const handler = (e) => {
            if (popRef.current && !popRef.current.contains(e.target)) {
                setSilencePopFor(null);
                setCustomHours('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [silencePopFor]);

    useEffect(() => {
        if (!visible) return;
        const handler = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [visible, onClose]);

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        return (alerts || []).filter((a) => {
            if (statusFilter && a.status !== statusFilter) return false;
            if (term) {
                const hay = `${a.title || ''} ${a.rule_code || ''}`.toLowerCase();
                if (!hay.includes(term)) return false;
            }
            return true;
        });
    }, [alerts, statusFilter, search]);

    const counts = useMemo(() => {
        const list = alerts || [];
        return {
            all:      list.length,
            open:     list.filter((a) => a.status === 'open').length,
            silenced: list.filter((a) => a.status === 'silenced').length,
            ignored:  list.filter((a) => a.status === 'ignored').length,
            resolved: list.filter((a) => a.status === 'resolved').length,
        };
    }, [alerts]);

    if (!visible) return null;

    const handleCardClick = (alert) => {
        if (!alert.seen) onMarkSeen(alert.id);
    };

    const stop = (e) => e.stopPropagation();

    const applySilence = (id, hours) => {
        onSilence(id, hours);
        setSilencePopFor(null);
        setCustomHours('');
    };

    return (
        <div css={alertsPanelCSS()} onClick={onClose}>
            <div className="ap-container" onClick={stop} role="dialog" aria-modal="true">
                <div className="ap-header">
                    <div className="ap-title-block">
                        <span className="ap-title-icon"><i className="pi pi-bell" /></span>
                        <h2 className="ap-title">Alertas operativas</h2>
                    </div>
                    <div className="ap-header-actions">
                        <button
                            type="button"
                            className="ap-icon-btn refresh"
                            onClick={onRefresh}
                            disabled={loading}
                            aria-label="Actualizar"
                            title="Actualizar"
                        >
                            <i className={`pi pi-refresh ${loading ? 'pi-spin' : ''}`} />
                        </button>
                        <button
                            type="button"
                            className="ap-icon-btn"
                            onClick={onClose}
                            aria-label="Cerrar"
                            title="Cerrar"
                        >
                            <i className="pi pi-times" />
                        </button>
                    </div>
                </div>

                <div className="ap-filters">
                    <div className="ap-status-pills">
                        {STATUSES.map((s) => {
                            const n = s.value ? counts[s.value] : counts.all;
                            return (
                                <button
                                    key={s.value || 'all'}
                                    type="button"
                                    className={`ap-pill ${statusFilter === s.value ? 'active' : ''}`}
                                    onClick={() => {
                                        setStatusFilter(s.value);
                                        if (listRef.current) listRef.current.scrollTop = 0;
                                    }}
                                >
                                    {s.label} ({n})
                                </button>
                            );
                        })}
                    </div>
                    <div className="ap-search-box">
                        <i className="pi pi-search" />
                        <input
                            type="text"
                            className="ap-search-input"
                            placeholder="Buscar por título o regla..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>

                <div className="ap-list" ref={listRef}>
                    {loading && (alerts || []).length === 0 && (
                        <>
                            {[0, 1, 2, 3, 4].map((i) => (
                                <div key={i} className="ap-skeleton">
                                    <div className="ap-skeleton-icon" />
                                    <div className="ap-skeleton-body">
                                        <div className="ap-skeleton-line mid" />
                                        <div className="ap-skeleton-line long" />
                                        <div className="ap-skeleton-line short" />
                                    </div>
                                </div>
                            ))}
                        </>
                    )}

                    {!loading && filtered.length === 0 && (
                        <div className="ap-empty">
                            <div className="ap-empty-icon"><i className="pi pi-check-circle" /></div>
                            <p className="ap-empty-title">Todo bajo control</p>
                            <p className="ap-empty-sub">No hay alertas que coincidan con los filtros.</p>
                        </div>
                    )}

                    {filtered.map((a) => {
                        const sev = sevFor(a.severity);
                        const isOpen = a.status === 'open';
                        const popOpen = silencePopFor === a.id;
                        const busy = pending[a.id];
                        return (
                            <div
                                key={a.id}
                                className={`ap-card ${a.seen ? '' : 'unseen'} ${busy ? 'busy' : ''}`}
                                style={{
                                    '--sev-border': sev.border,
                                    '--sev-bg': sev.bg,
                                    '--sev-fg': sev.fg,
                                }}
                                onClick={() => !busy && handleCardClick(a)}
                            >
                                <span className="ap-card-icon"><i className={ruleIcon(a.rule_code)} /></span>

                                <div className="ap-card-body">
                                    <div className="ap-card-title-row">
                                        <span className="ap-card-title">{a.title}</span>
                                        <span className="ap-sev-badge">{sev.label}</span>
                                    </div>
                                    <div className="ap-card-message">{a.message}</div>
                                    <div className="ap-card-meta">
                                        <span>{ruleLabel(a.rule_code)}</span>
                                        <span className="sep" />
                                        <span>{timeAgo(a.detected_at)}</span>
                                        <span className="sep" />
                                        <span className={`ap-status-chip ${a.status}`}>
                                            {STATUS_LABELS[a.status] || a.status}
                                        </span>
                                    </div>
                                </div>

                                {isOpen && (
                                    <div className="ap-card-actions" onClick={stop}>
                                        <button
                                            type="button"
                                            className="ap-action-btn ignore"
                                            onClick={() => onIgnore(a.id)}
                                            title="Ignorar"
                                            aria-label="Ignorar"
                                            disabled={!!busy}
                                        >
                                            <i className={busy === 'ignore' ? 'pi pi-spin pi-spinner' : 'pi pi-ban'} />
                                        </button>
                                        <button
                                            type="button"
                                            className="ap-action-btn silence"
                                            onClick={() => {
                                                setSilencePopFor(popOpen ? null : a.id);
                                                setCustomHours('');
                                            }}
                                            title="Silenciar"
                                            aria-label="Silenciar"
                                            disabled={!!busy}
                                        >
                                            <i className={busy === 'silence' ? 'pi pi-spin pi-spinner' : 'pi pi-clock'} />
                                        </button>
                                        <button
                                            type="button"
                                            className="ap-action-btn resolve"
                                            onClick={() => onResolve(a.id)}
                                            title="Resolver"
                                            aria-label="Resolver"
                                            disabled={!!busy}
                                        >
                                            <i className={busy === 'resolve' ? 'pi pi-spin pi-spinner' : 'pi pi-check'} />
                                        </button>

                                        {popOpen && (
                                            <div className="ap-silence-pop" ref={popRef}>
                                                <p className="ap-silence-label">Silenciar por</p>
                                                <div className="ap-silence-chips">
                                                    {QUICK_SILENCE.map((q) => (
                                                        <button
                                                            key={q.hours}
                                                            type="button"
                                                            className="ap-silence-chip"
                                                            onClick={() => applySilence(a.id, q.hours)}
                                                        >
                                                            {q.label}
                                                        </button>
                                                    ))}
                                                </div>
                                                <div className="ap-silence-custom">
                                                    <input
                                                        type="number"
                                                        min={1}
                                                        placeholder="horas"
                                                        value={customHours}
                                                        onChange={(e) => setCustomHours(e.target.value)}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const h = parseInt(customHours, 10);
                                                            if (h > 0) applySilence(a.id, h);
                                                        }}
                                                    >
                                                        OK
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="ap-footer">
                    <span className="count">{counts.open}</span>&nbsp;abiertas
                    &nbsp;·&nbsp;
                    <span className="count">{counts.silenced}</span>&nbsp;silenciadas
                    &nbsp;·&nbsp;
                    <span className="count">{counts.ignored}</span>&nbsp;ignoradas
                    &nbsp;·&nbsp;
                    <span className="count">{counts.resolved}</span>&nbsp;resueltas
                </div>
            </div>
        </div>
    );
}
